import { castArray, omit } from 'lodash-es';
import CancellationError from '@/common/utils/errors/CancellationError';

type ThrottledActionFn = (abortController: AbortController) => Promise<void>;

type ThrottledCall = {
  run: () => void;
  cancel: () => void;
  state: 'idle' | 'pending';
}

type ActionFunction<T> = (id: ID | ID[], options?: { abortController?: AbortController }) => Promise<T>;

type CallbackFunction<T> = (result: T, idsProcessed: ID[]) => void;

export type AccumulatedAction = {
  (id: ID | ID[]): void;
  clearId: (id: ID | ID[]) => void;
  clear: () => void;
}

// @cpt-state:cpt-pstorageuihci-state-common-pinia-store-throttled-call:p1
// @cpt-dod:cpt-pstorageuihci-dod-common-pinia-store-state-getter:p1
const createThrottledCall = (action: ThrottledActionFn, threshold: number): ThrottledCall => {
  let state: 'idle' | 'pending' = 'idle';
  let chainCall: (() => void) | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let timestamp: number | undefined;
  let abortController: AbortController | null = null;

  const clear = (): void => {
    state = 'idle';
    clearTimeout(timeout);
    timestamp = undefined;
    chainCall = undefined;
  };

  const cancel = (): void => {
    // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-throttled-call:p1:inst-to-idle-cancel
    if (abortController) {
      abortController.abort();
    } else {
      clear();
    }
    // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-throttled-call:p1:inst-to-idle-cancel
  };

  const run = (): void => {
    if (threshold && timestamp && Date.now() - timestamp < threshold) {
      if (state === 'pending') {
        chainCall = () => run();
      }

      return;
    }

    cancel();
    const timeoutDelay = timestamp ? threshold - (Date.now() - timestamp) : 0;
    timestamp = Date.now();
    timeout = setTimeout(async () => {
      try {
        // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-throttled-call:p1:inst-to-pending
        state = 'pending';
        // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-throttled-call:p1:inst-to-pending
        abortController = new AbortController();
        await action(abortController);

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-chain
        if (chainCall) {
          const chainCallDup = chainCall;
          clear();
          chainCallDup();
        }
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-chain
      } finally {
        // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-throttled-call:p1:inst-to-idle-complete
        clear();
        // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-throttled-call:p1:inst-to-idle-complete
        abortController = null;
      }
    }, timeoutDelay);
  };

  return {
    run,
    cancel,
    // @cpt-begin:cpt-pstorageuihci-dod-common-pinia-store-state-getter:p1
    get state() {
      return state;
    }
    // @cpt-end:cpt-pstorageuihci-dod-common-pinia-store-state-getter:p1
  };
};

// @cpt-algo:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1
export default function useAccumulatedAction<T>(
  action: ActionFunction<T>,
  callback: CallbackFunction<T> = () => {},
  threshold = 2000
): AccumulatedAction {
  const ids: ID[] = [];

  const actionFn = async (abortController: AbortController): Promise<void> => {
    let res: T;

    const idsToProcess = ids.slice();
    ids.length = 0;

    if (idsToProcess.length === 0) {
      return;
    }

    try {
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-execute
      res = await action(idsToProcess, { abortController });
      callback(res, idsToProcess);
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-execute
    } catch (error) {
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-cancellation
      if (error instanceof CancellationError) {
        idsToProcess.forEach(id => !ids.includes(id) && ids.push(id));
        return;
      }
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-cancellation

      throw error;
    }
  };

  const throttled = createThrottledCall(actionFn, threshold);
  const runner = {
    // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-accumulate
    run: (id: ID | ID[]) => {
      castArray(id)
        .forEach(idItem => !ids.includes(idItem) && ids.push(idItem));
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-accumulate
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-schedule
      return throttled.run();
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-schedule
    },
    // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-clear-id
    clearId: (id: ID | ID[]) => {
      if (throttled.state === 'pending') {
        throttled.cancel();
      }
      castArray(id)
        .forEach(idItem => {
          if (ids.includes(idItem)) {
            ids.splice(ids.indexOf(idItem), 1);
          }
        });

      if (ids.length) {
        throttled.run();
      } else {
        throttled.cancel();
      }
    },
    // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-clear-id
    // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-clear
    clear: () => {
      ids.length = 0;
      throttled.cancel();
    }
    // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-accumulated-action:p1:inst-clear
  };

  const accumulatedAction = Object.assign(runner.run, omit(runner, 'run'));
  return accumulatedAction;
}
